import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGlobe, FiLock, FiClock, FiFile, FiCode, FiImage, FiFileText, FiExternalLink, FiArrowRight } = FiIcons;

function WorkEntryCard({ entry }) {
  const getArtifactIcon = (type) => {
    switch (type) {
      case 'code': return FiCode;
      case 'design': return FiImage;
      case 'doc': return FiFileText;
      default: return FiFile;
    }
  };

  const getVisibilityInfo = (visibility) => {
    return visibility === 'public' 
      ? { icon: FiGlobe, color: 'text-indigo-400', label: 'Public' } 
      : { icon: FiLock, color: 'text-slate-500', label: 'Private' };
  };

  const visibilityInfo = getVisibilityInfo(entry.visibility);
  const timeAgo = formatDistanceToNow(new Date(entry.timestamp), { addSuffix: true });

  return (
    <motion.div 
      whileHover={{ y: -4, scale: 1.01 }}
      className="bg-white/5 border border-white/10 rounded-[2rem] p-8 hover:bg-white/[0.08] hover:border-white/20 transition-all group relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 p-6 opacity-0 group-hover:opacity-100 transition-opacity">
        <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-white -rotate-45" />
      </div>

      <Link to={`/entry/${entry.id}`} className="block">
        <div className="flex flex-col md:flex-row md:items-start justify-between mb-6 gap-4">
          <div className="space-y-1">
            <h3 className="text-2xl font-black text-white tracking-tighter italic uppercase group-hover:text-indigo-300 transition-colors">
              {entry.title}
            </h3>
            <div className="flex items-center space-x-3 text-[10px] font-black uppercase tracking-widest text-slate-500">
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiClock} className="w-3 h-3" />
                <span>{entry.duration} Flow</span>
              </div>
              <span className="w-1 h-1 bg-slate-700 rounded-full" />
              <span>{timeAgo}</span>
            </div>
          </div>
          
          <div className={`flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest ${visibilityInfo.color}`}>
            <SafeIcon icon={visibilityInfo.icon} className="w-3 h-3" />
            <span>{visibilityInfo.label}</span>
          </div>
        </div>

        <p className="text-slate-400 font-medium leading-relaxed mb-8 max-w-2xl">
          {entry.summary}
        </p>

        {entry.artifacts && entry.artifacts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {entry.artifacts.map((artifact, index) => (
              <div 
                key={index} 
                className="flex items-center space-x-2 bg-white/5 border border-white/5 px-4 py-2 rounded-xl text-xs font-bold text-slate-300 group-hover:border-white/10 transition-colors"
              >
                <SafeIcon icon={getArtifactIcon(artifact.type)} className="w-3 h-3 text-indigo-400" />
                <span>{artifact.name}</span>
                <span className="text-slate-600">({artifact.size})</span>
              </div>
            ))}
          </div>
        )}
      </Link>
    </motion.div>
  );
}

export default WorkEntryCard;